import * as BABYLON from 'babylonjs';

export interface ColorInfo {
    componentType: "FLOAT32" | "UNSIGNED_BYTE";
    normalized: boolean;
    components: number;
}

export class GeometryData {
    public id: string;
    public name: string;
    public positions: Float32Array;
    public normals: Float32Array;
    public indices: Uint32Array;
    public uvs: Float32Array;
    public colors: Float32Array | Uint8Array;
    public colorInfo: ColorInfo;
}

export class Utils {
    private static _defaultColorInfo: ColorInfo = {
        componentType: "FLOAT32",
        normalized: false,
        components: 4
    };

        static create(
            name: string,
            id: string,
            positions: number[] | Float32Array,
            indices: BABYLON.IndicesArray,
            normals?: number[] | Float32Array,
            uvs?: number[] | Float32Array,
            colors?: number[] | Float32Array | Uint8Array,
            colorInfo?: ColorInfo
        ): GeometryData {
            var geometry = new GeometryData();

            geometry.name = name || id;
            geometry.id = id || name;
            geometry.positions = positions instanceof Float32Array ? new Float32Array(positions) : new Float32Array(positions || []);
            geometry.normals = normals instanceof Float32Array ? new Float32Array(normals) : new Float32Array(normals || []);
            geometry.indices = this.cloneIndices(indices);
            geometry.uvs = uvs instanceof Float32Array ? new Float32Array(uvs) : new Float32Array(uvs || []);
            geometry.colors = this.cloneColors(colors);
            geometry.colorInfo = this.cloneColorInfo(colorInfo);

            return geometry;
        }

        static clone(source: GeometryData, id?: string, name?: string): GeometryData {
            if (source == null) {
                return null;
            }

            return this.create(
                name || source.name,
                id || source.id,
                source.positions,
                source.indices,
                source.normals,
                source.uvs,
                source.colors,
                source.colorInfo
            );
        }

        static fromMesh(mesh: BABYLON.Mesh, disposeSource: boolean = false): GeometryData {
            if (mesh == null || mesh.geometry == null) {
                return null;
            }

            mesh.computeWorldMatrix(true);

            var colorData = this.extractColors(mesh.geometry);
            var geometry = this.create(
                mesh.name,
                mesh.id,
                this.extractFloatVertexData(mesh, BABYLON.VertexBuffer.PositionKind),
                mesh.getIndices() || [],
                this.extractFloatVertexData(mesh, BABYLON.VertexBuffer.NormalKind),
                this.extractFloatVertexData(mesh, BABYLON.VertexBuffer.UVKind),
                colorData.colors,
                colorData.colorInfo
            );

            this.applyTransformInPlace(geometry, mesh.getWorldMatrix());

            if (disposeSource) {
                mesh.dispose();
            }

            return geometry;
        }

        static fromMeshes(meshes: BABYLON.Mesh[], disposeSource: boolean = false): GeometryData[] {
            var retVal: GeometryData[] = [];

            for (var i = 0; i < meshes.length; i++) {
                var geometry = this.fromMesh(meshes[i], disposeSource);

                if (geometry != null) {
                    retVal.push(geometry);
                }
            }

            return retVal;
        }

        static transform(source: GeometryData, matrix: BABYLON.Matrix, id?: string, name?: string): GeometryData {
            var geometry = this.clone(source, id, name);

            return this.applyTransformInPlace(geometry, matrix);
        }

        static applyTransformInPlace(geometry: GeometryData, matrix: BABYLON.Matrix): GeometryData {
            if (geometry == null || matrix == null) {
                return geometry;
            }

            var transformed = BABYLON.Vector3.Zero();
            var value = BABYLON.Vector3.Zero();
            var index: number;

            if (geometry.positions != null && geometry.positions.length > 0) {
                for (index = 0; index < geometry.positions.length; index += 3) {
                    BABYLON.Vector3.FromArrayToRef(geometry.positions, index, value);
                    BABYLON.Vector3.TransformCoordinatesToRef(value, matrix, transformed);

                    geometry.positions[index] = transformed.x;
                    geometry.positions[index + 1] = transformed.y;
                    geometry.positions[index + 2] = transformed.z;
                }
            }

            if (geometry.normals != null && geometry.normals.length > 0) {
                for (index = 0; index < geometry.normals.length; index += 3) {
                    BABYLON.Vector3.FromArrayToRef(geometry.normals, index, value);
                    BABYLON.Vector3.TransformNormalToRef(value, matrix, transformed);

                    geometry.normals[index] = transformed.x;
                    geometry.normals[index + 1] = transformed.y;
                    geometry.normals[index + 2] = transformed.z;
                }
            }

            return geometry;
        }

        static getVertexCount(geometry: GeometryData): number {
            return geometry != null && geometry.positions != null ? geometry.positions.length / 3 : 0;
        }

        static getIndexCount(geometry: GeometryData): number {
            return geometry != null && geometry.indices != null ? geometry.indices.length : 0;
        }

        static getUvCount(geometry: GeometryData): number {
            return geometry != null && geometry.uvs != null ? geometry.uvs.length : 0;
        }

        static hasNormals(geometry: GeometryData): boolean {
            return geometry != null && geometry.normals != null && geometry.normals.length > 0;
        }

        static hasColors(geometry: GeometryData): boolean {
            return geometry != null && geometry.colors != null && geometry.colors.length > 0;
        }

        static getColorSignature(geometry: GeometryData): string {
            if (!this.hasColors(geometry)) {
                return "none";
            }

            var colorInfo = geometry.colorInfo || this._defaultColorInfo;

            return colorInfo.componentType + ":" + (colorInfo.normalized ? "1" : "0") + ":" + colorInfo.components;
        }

        static cloneColorInfo(colorInfo?: ColorInfo): ColorInfo {
            var info = colorInfo || this._defaultColorInfo;

            return {
                componentType: info.componentType,
                normalized: info.normalized,
                components: info.components
            };
        }

        static cloneIndices(indices?: BABYLON.IndicesArray): Uint32Array {
            if (indices == null) {
                return new Uint32Array(0);
            }

            if (indices instanceof Uint32Array) {
                return new Uint32Array(indices);
            }

            if (indices instanceof Int32Array || indices instanceof Uint16Array || indices instanceof Int16Array || indices instanceof Uint8Array || indices instanceof Int8Array) {
                return new Uint32Array(indices);
            }

            return new Uint32Array(indices as ArrayLike<number>);
        }

        private static cloneColors(colors?: number[] | Float32Array | Uint8Array): Float32Array | Uint8Array {
            if (colors instanceof Uint8Array) {
                return new Uint8Array(colors);
            }

            if (colors instanceof Float32Array) {
                return new Float32Array(colors);
            }

            if (colors != null) {
                return new Float32Array(colors);
            }

            return new Float32Array(0);
        }

        private static extractColors(geometry: BABYLON.Geometry): { colors: Float32Array | Uint8Array; colorInfo: ColorInfo; } {
            var vbColor = geometry.getVertexBuffer(BABYLON.VertexBuffer.ColorKind);

            if (vbColor == null) {
                return {
                    colors: new Float32Array(0),
                    colorInfo: this.cloneColorInfo()
                };
            }

            var data = vbColor.getData() as ArrayBufferView;
            var components = vbColor.getSize() || 4;
            var isUnsignedByte = vbColor.type === BABYLON.VertexBuffer.UNSIGNED_BYTE;

            if (data != null && data.buffer != null) {
                if (isUnsignedByte) {
                    return {
                        colors: new Uint8Array(data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength)),
                        colorInfo: {
                            componentType: "UNSIGNED_BYTE",
                            normalized: !!vbColor.normalized,
                            components: components
                        }
                    };
                }

                return {
                    colors: new Float32Array(data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength)),
                    colorInfo: {
                        componentType: "FLOAT32",
                        normalized: false,
                        components: components
                    }
                };
            }

            return {
                colors: new Float32Array(geometry.getVerticesData(BABYLON.VertexBuffer.ColorKind) || []),
                colorInfo: {
                    componentType: "FLOAT32",
                    normalized: false,
                    components: components
                }
            };
        }

        private static extractFloatVertexData(mesh: BABYLON.Mesh, kind: string): Float32Array {
            var vertexBuffer = mesh.getVertexBuffer(kind) || mesh.geometry?.getVertexBuffer(kind);

            if (vertexBuffer != null) {
                var data = vertexBuffer.getData() as ArrayBufferView | number[] | null;

                if (data instanceof Float32Array) {
                    return new Float32Array(data);
                }

                if (ArrayBuffer.isView(data)) {
                    return new Float32Array(Array.from((data as any) as ArrayLike<number>));
                }

                if (data != null) {
                    return new Float32Array(data);
                }
            }

            return new Float32Array(mesh.getVerticesData(kind) || []);
        }
}
